import type { RunModificationsReceipt } from '../lib/runModifications'
import type { GithubPreviewConfig } from '../schemas/githubPreview'
import type { TestRunFlags } from '../schemas/testRunFlags'
import type { BugFixPrSchema } from '../schemas/projectListSchemas'
import type { BugFixResult } from '../../netlify/functions/lib/bug-management/github-app/fix-policy'
import type { z } from 'zod'
import type { SecurityHistoryEntrySchema } from '../schemas/securityReactivation'
import type {
  AnalysisSection,
  BugAnalysis,
  ChronologyStep,
  EvidenceItem,
} from '../../netlify/functions/lib/bug-management/report-schema'
import type { ReverseProxyStatus } from '../../netlify/functions/lib/reverse-proxy'

// Server-state shapes shared by RTK Query endpoints and view components.
// These types intentionally live outside Redux slices: server data is owned by RTK Query.

export type JourneySteps = unknown

export interface PageInteraction {
  id: string
  action: string
  type: string
}

export interface AppPage {
  id: string
  url: string
  interactions: PageInteraction[]
}

export interface GlobalVariable {
  name: string
  init?: string
}

export interface ProjectFlags {
  login_failed?: boolean
  app_load_failed?: boolean
  // The app is behind a security-verification wall QA cannot clear (a Cloudflare/Turnstile human-check,
  // hCaptcha, "checking your browser" interstitial). Set when an exploration reports a `captcha` obstacle;
  // means QA never reached the app, so no part of it was tested.
  verification_wall?: boolean
}

export type GithubMainBranchTestMode = 'none' | 'detect' | 'events' | 'environment'
export type GithubPrTestMode = 'none' | 'events' | 'environment'

export interface GithubSettings {
  repoUrl: string
  mainBranch: string
  mainBranchTesting: GithubMainBranchTestMode
  mainBranchDeploymentUrl: string | null
  prTesting: GithubPrTestMode
  prPreview?: GithubPreviewConfig | null
  fileIssues: boolean
  accessTokenSet: boolean
}

export interface GithubAppStatus {
  appConfigured: boolean
  slug: string
  repoFullName: string | null
  installed: boolean
  installationId: string | null
  ownerInstalled: boolean
  ownerInstallationId: string | null
  existingProject: { id: string; name: string } | null
  defaultBranch: string | null
}

/** One GitHub App installation and the repositories it can reach (`/api/github-app-installation`). */
export interface GithubAppInstallation {
  installationId: string
  accountLogin: string | null
  suspended: boolean
  repositories: {
    fullName: string
    private: boolean
    defaultBranch: string
    htmlUrl: string
    repoKey: string | null
    /** Set when a project the caller belongs to already connects this repo. */
    existingProjectId: string | null
  }[]
}

export type ReverseProxyEventType = 'provisioning' | 'server_up' | 'server_down' | 'client_connected' | 'client_disconnected'

export interface ReverseProxyEvent {
  id: number
  type: ReverseProxyEventType
  detail: string | null
  at: string
}

export type IssueTrackerProvider = 'github' | 'gitlab' | 'linear' | 'jira' | 'asana'

export interface IssueTrackerSettings {
  provider: IssueTrackerProvider
  enabled: boolean
  githubRepoUrl?: string
  gitlabProjectUrl?: string
  linearTeamId?: string
  jiraBaseUrl?: string
  jiraProjectKey?: string
  jiraEmail?: string
  jiraIssueType?: string
  asanaProjectUrl?: string
  apiTokenSet: boolean
}

// Where a project's completed daily summaries are published. The Slack/Discord webhook URLs are
// secrets held server-side; clients see only `webhookUrlSet`. See
// netlify/functions/lib/summary-destinations.ts.
export type SummaryEmailRecipients = 'owner' | 'members' | 'custom'

export interface SummaryDestinations {
  slack?: { webhookUrlSet: boolean } | null
  discord?: { webhookUrlSet: boolean } | null
  email?: { recipients: SummaryEmailRecipients; addresses: string[] } | null
}

export interface Project {
  id: string
  name: string
  target_url: string | null
  webhook_url: string | null
  finished_webhook_url: string | null
  completion_email_pending?: boolean
  completion_email_to?: string | null
  backend_recording_url: string | null
  backend_log_url: string | null
  auto_submit_bugs: string | boolean
  status: string
  paused_reason?: string | null
  pages: AppPage[]
  max_containers: number
  budget: number | null
  parallel_mcp_enabled: boolean
  max_parallel_containers: number
  owner_id: string | null
  /**
   * The team that owns the project, and whose plan therefore funds it. Null for a project created
   * before teams existed and never adopted. Always present in the API response (the row is returned
   * whole); declared here because the guest-invite surface needs to know which plan applies.
   */
  team_id?: string | null
  is_public: boolean
  share_location: string | null
  global_variables: GlobalVariable[]
  design_document: string | null
  recording_id: string | null
  enabled_polish_passes: string[]
  // Present on a response to a write that touched the bug types (lib/run-modifications.ts).
  run_modifications?: RunModificationsReceipt
  // Security-pass ownership state. The nonce is not a secret from its own owner — proving control of a
  // site by publishing it is the entire point — so it is served to project members like any other
  // setting, and the settings UI shows it so an interrupted setup can be finished.
  security_probing_enabled?: boolean
  security_nonce?: string | null
  security_ownership_exempt?: boolean
  use_reverse_proxy: boolean
  reverse_proxy_status: string
  github: GithubSettings | null
  issue_tracker: IssueTrackerSettings | null
  summary_destinations?: SummaryDestinations | null
  og_image_url: string | null
  og_image_version: number
  app_screenshot_url?: string | null
  // The newest task-thumbnail frame in both stored resolutions: the preview above (a <=480px
  // downscale) and its high-res companion, with intrinsic pixel widths serving as srcset width
  // descriptors (see ProjectList.tsx). Null on frames captured before the companion existed —
  // those have no responsive candidates and keep the single-src behavior.
  app_screenshot_width?: number | null
  app_screenshot_full_url?: string | null
  app_screenshot_full_width?: number | null
  flags: ProjectFlags
  user_role?: string
  bug_count?: number
  last_test_run?: string | null
  created_at: string
  updated_at: string
  deleted_at?: string | null
}

/** One project's Insights aggregates, as `/api/insights` returns them. */
export interface ProjectInsightCounts {
  project_id: string
  bugCounts: {
    total: number
    journeyBugs: number
    polishBugs: number
    openCount: number
    resolvedCount: number
  }
  /** Bug count per lower-cased severity. */
  severityCounts: Record<string, number>
  /** Bug count per polish_category, for polish-filed bugs only. */
  polishCategories: Record<string, number>
  journeyCount: number
  explorationCount: number
}

/** A project paired with its aggregates — what the Insights charts render. */
export interface ProjectInsight extends ProjectInsightCounts {
  project: Project
}

export interface ProjectMember {
  id: string
  project_id: string
  user_id: string
  email: string | null
  /** Display name resolved from the member's login session; null when they haven't signed in yet. */
  name?: string | null
  role: string
  created_at: string
}

export type TeamRole = 'owner' | 'admin' | 'member'

/**
 * A team as the signed-in user sees it — the account that owns projects and pays for them (mirrors
 * the /api/teams summary). Every user owns exactly one and can belong to any number of others.
 *
 * The permission flags are computed server-side so one definition decides what the UI offers. They
 * are not interchangeable: `can_manage` covers the roster and the team name, while
 * `can_manage_billing` covers the funding workspace's Stripe portal. Both are owner-or-admin today,
 * but `can_manage_billing` is additionally subject to the Replay workspace's own role check — passing
 * it means the UI offers the action, not that the backend will grant it (see docs/backend/teams.md).
 */
export interface Team {
  id: string
  name: string
  role: TeamRole
  member_count: number
  is_owner: boolean
  can_manage: boolean
  can_manage_billing: boolean
  /** The shareable self-join URL. Null for a plain member, and until a link has been created. */
  invite_url: string | null
}

export interface TeamMember {
  id: string
  team_id: string
  user_id: string
  email: string | null
  /** Display name from their login session; null when they haven't signed in yet (pending invite). */
  name?: string | null
  role: TeamRole
  created_at: string
}

/** Brand-safe team summary for the logged-out /join/:token landing page. */
export interface TeamInviteInfo {
  id: string
  name: string
  member_count: number
}

export type CommentTargetType = 'bug' | 'test_run'

export interface Comment {
  id: string
  project_id: string
  target_type: CommentTargetType
  target_id: string
  author_id: string
  author_name: string | null
  author_email: string | null
  content: string
  mentioned_user_ids: string[]
  created_at: string
}

export type NotificationType = 'comment' | 'mention'

export interface AppNotification {
  id: string
  user_id: string
  project_id: string
  type: NotificationType
  target_type: CommentTargetType
  target_id: string
  comment_id: string | null
  actor_name: string | null
  content: string | null
  read_at: string | null
  created_at: string
}

export interface ReverseProxyStatusResponse extends ReverseProxyStatus {
  enabled: boolean
  reverse_proxy_status?: string
  target_url?: string | null
}

export interface VersionLog {
  id: string
  task_id?: string | null
  message: string
  log_type: string
  created_at: string
}

export interface VersionJourneyChange {
  journey_id: string | null
  journey_name: string | null
  action: 'created' | 'updated' | 'deleted'
  version_number: number | null
  step_count: number | null
  created_at: string
  mobile?: boolean
  child_count?: number
  last_run_id?: string | null
  last_run_status?: string | null
  preview_run_id?: string | null
  last_run_bug_id?: string | null
  last_run_bug_status?: string | null
  last_run_bug_title?: string | null
}

export interface VersionBug {
  id: string
  title: string
  severity: string
  status: string
  polish_category: string | null
  replay_recording_id: string | null
  test_run_id: string | null
  discovered_at: string
  version_id: string | null
  is_duplicate: boolean
}

export interface VersionExplorationJourney {
  journey_id: string
  journey_name: string | null
  action: 'created' | 'updated'
  step_count: number | null
}

export interface VersionExploration {
  id: string
  prompt: string
  status: string
  journeys_created: number
  journeys_updated: number
  created_at: string
  journeys: VersionExplorationJourney[]
}

export interface AssetFingerprint {
  url: string
  type: 'js' | 'css'
  hash: string
  bytes: number
}

export interface DeployedAppFingerprint {
  url: string
  fetchedAt: string
  assets: AssetFingerprint[]
  assetCount: number
  totalBytes: number
  combinedHash: string
}

export interface Version {
  id: string
  project_id: string
  git_sha: string
  branch_name: string
  deployed_url: string | null
  change_description: string | null
  status: string
  timestamp: string
  journeys_created: number
  journeys_updated: number
  journeys_deleted: number
  replay_recording_id?: string | null
  is_main_branch?: boolean
  deployed_app_fingerprint?: DeployedAppFingerprint | null
  created_at: string
  started_at?: string | null
  completed_at?: string | null
  logs?: VersionLog[]
  mcp_errors?: Array<{ id: string; container_name: string | null; recording_id: string | null; tool: string | null; params: unknown | null; kind: string | null; message: string | null; duration_ms: number | null; occurred_at: string | null; created_at: string }>
  journey_changes?: VersionJourneyChange[]
  explorations?: VersionExploration[]
  bugs?: VersionBug[]
  task_id?: string | null
}

export interface MonitorRun {
  id: string
  status: 'monitoring' | 'detected' | 'expired' | 'failed'
  trigger_sha: string | null
  detected_sha: string | null
  detected_version_id: string | null
  checks: number
  last_error: string | null
  started_at: string
  last_checked_at: string | null
  deadline: string
  ended_at: string | null
}

export interface MonitorInfo {
  active: MonitorRun | null
  runs: MonitorRun[]
}

export interface PullRequestScreenshot {
  id: string
  url: string
  full_url: string | null
  caption: string | null
  created_at: string
}

/**
 * What a COLLAPSED pull-request row shows. Served for a whole list (or a page of Test Runs) at a fixed
 * query cost — see netlify/functions/lib/github-app/pr-views.ts. The contents a row reveals when it is
 * expanded live on `PullRequestDetail`, fetched separately at that point.
 */
export interface PullRequest {
  pr_number: number
  repo_full_name: string
  pr_url: string
  /** The pull request's own title, or null when we don't have it. */
  pr_title: string | null
  branch_name: string
  integration_source: string
  head_sha: string
  run_status: string
  /** The PR's state on GitHub ('open' | 'closed' | 'merged'), not the state of its QA run. */
  pr_state: string
  version_id: string
  task_id: string | null
  version_status: string
  deployed_url: string | null
  awaiting_deployment: boolean
  awaiting_deployment_since: string
  replay_recording_id: string | null
  approval: 'approved' | 'blocked' | 'pending' | 'cancelled'
  bug_count: number
  journeys_count: number
  bugs: VersionBug[]
  updated_at: string
  created_at: string
}

/** One PR's expanded contents, loaded when its row is opened. */
export interface PullRequestDetail extends PullRequest {
  bug_verifications: BugFixResult[]
  journey_changes: VersionJourneyChange[]
  screenshots: PullRequestScreenshot[]
}

export type { EvidenceItem, AnalysisSection, ChronologyStep, BugAnalysis }
// The frame a chronology step displays, derived from its Screenshot evidence — re-exported so views read
// it from the same place they get the types.
export { stepScreenshotUrl } from '../../netlify/functions/lib/bug-management/report-schema'

// Frontend copy of src/guidance/bug-management/authoring.ts `JudgeResult` (app code does not import src/guidance).
export interface JudgeResult {
  approved: boolean
  assessment?: string
  problems: string[]
  judge_type?: 'evidence' | 'requirements'
}

export interface JudgeSubmission {
  attempt: number
  submitted_at: string
  submitted_data?: {
    title?: string
    description?: string | null
    analysis?: Record<string, unknown> | null
  }
  result: JudgeResult
  evidence_result?: JudgeResult
  requirements_result?: JudgeResult
}

export interface ScreenshotSpec {
  screenshot_url: string
  box?: { x: number; y: number; width: number; height: number } | null
}

export interface BugListItem {
  fix_prs?: (Pick<z.infer<typeof BugFixPrSchema>, 'repo_full_name' | 'pr_number'> & { active?: boolean })[]
  analysis?: Pick<BugAnalysis, 'impact'> | null
  id: string
  project_id: string
  severity: string
  title: string
  status: string
  description?: string | null
  test_run_id?: string | null
  sent_to_webhook?: boolean
  polish_category?: string | null
  environment_id?: string | null
  environment_name?: string | null
  environment_kind?: string | null
  pr_number?: number | null
  pr_repo_full_name?: string | null
}

export interface Bug extends BugListItem {
  fix_prs?: z.infer<typeof BugFixPrSchema>[]
  description: string | null
  reproduction_steps: string | null
  expected_behavior: string | null
  actual_behavior: string | null
  replay_recording_id: string | null
  test_run_replay_recording_ids: Record<string, string> | null
  notes: string | null
  discovered_at: string
  test_run_id: string | null
  task_id?: string | null
  journey_id?: string | null
  journey_name?: string | null
  project_name?: string
  version_branch?: string | null
  version_git_sha?: string | null
  sent_to_webhook: boolean
  analysis: BugAnalysis | null
  judge_assessment: JudgeResult | null
  judge_submissions: JudgeSubmission[] | null
  polish_category: string | null
  video_spec: Record<string, unknown> | null
  video_url: string | null
  screenshot_spec: ScreenshotSpec | null
  duplicates?: Array<{ id: string; new_title: string; new_description?: string | null; detected_at: string }> | null
}

export interface BugBreakdownRow {
  category: string
  critical: number
  high: number
  medium: number
  low: number
}

export interface TaskSummary {
  id: string
  project_id: string
  test_run_id: string | null
  journey_id: string | null
  exploration_id: string | null
  goal: string | null
  status: string
  created_at: string
  started_at: string | null
  completed_at: string | null
  project_name?: string
  journey_name?: string | null
  journey_version_number?: number | null
  kind?: string | null
}

export interface Task extends TaskSummary {
  container_name?: string | null
  container_url?: string | null
}

export function taskKindLabel(task: TaskSummary): string {
  if (task.exploration_id) return 'Exploration'
  if (task.test_run_id) return 'Test Run'
  return 'Task'
}

export interface TaskHistoryStats {
  total: number
  completed: number
  failed: number
  total_duration_seconds: number
  avg_duration_seconds: number
}

export interface TaskHistory {
  tasks: TaskSummary[]
  stats: TaskHistoryStats
  page: number
  pageSize: number
}

export interface ProjectTaskActivity {
  stats: TaskHistoryStats
  recent: TaskSummary[]
}

export interface DuplicateDetection {
  id: string
  new_title: string
  new_description: string | null
  existing_bug_id: string
  existing_bug_title: string
  existing_bug_severity: string
  existing_bug_status: string
  detected_at: string
}

export interface CreatedJourney {
  id: string
  name: string
  description: string | null
  created_at: string
  steps: unknown
  summary: string | null
  step_count: number
  judge_status: string | null
  judge_submissions: Array<{ attempt: number; result: { approved: boolean; assessment: string; problems: string[]; revised_description?: string } }> | null
}

export interface SecurityFindingSummary {
  kind: string
  severity: string
  element: string | null
  description: string | null
}

export type SecurityAuditStatus = 'exception' | 'no-exceptions-observed' | 'limited' | 'not-tested'

export interface SecurityAuditResult {
  control: string
  title: string
  status: SecurityAuditStatus
  finding_count: number
  finding_kinds: string[]
  tests: string[]
  limitation: string
}

export interface SecurityScanSummary {
  security_bug_count: number
  id: string
  status: string
  reason: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
  test_session_id: string
  targets_identified: number
  targets: Array<{
    url: string
    method: string
    params: string[]
    id_params: string[]
    source?: 'observed' | 'conventional' | 'derived' | 'unrecorded'
    probe_groups: SecurityProbeGroupSummary[]
    request_count: number
    bug_ids: string[]
  }>
  general_groups: SecurityProbeGroupSummary[]
  request_details_available: number
  requests_made: number
  verified_origins: string[]
  ownership_waived: boolean
  identities_configured: number
  identities_acquired: string[]
  stopped_reason: string | null
  warnings: string[]
  findings: SecurityFindingSummary[]
  audit_coverage: { passive_reviewed: boolean; active_probed: boolean; passive_limited: boolean; active_limited: boolean }
  audit_results: SecurityAuditResult[]
}

export interface SecurityProbeRequestSummary {
  method: string
  url: string
  status: number | null
  narration: string
  duration_ms?: number
  error?: string
  operation?: string
}

export interface SecurityProbeGroupSummary {
  operation: string
  label: string
  description: string
  request_count: number
  requests?: SecurityProbeRequestSummary[]
  findings: Array<SecurityFindingSummary & { bug_ids: string[] }>
  bug_ids: string[]
  warnings: string[]
}

export interface ProjectSecurityResults {
  history?: z.infer<typeof SecurityHistoryEntrySchema>[]
  request_details_included: boolean
  scans: SecurityScanSummary[]
  bugs: BugListItem[]
}

export interface JourneyVersion {
  id: string
  journey_id: string
  version_number: number
  name: string
  description: string | null
  instructions: string | null
  steps: JourneySteps
  summary: string | null
  step_count: number
  exploration_id: string | null
  created_at: string
  last_run_status: string | null
  last_run_at: string | null
  last_run_error: string | null
  run_count: number
  last_run_bug_id: string | null
  last_run_bug_status: string | null
  last_run_bug_title: string | null
}

export interface JourneyJudgeResult {
  approved: boolean
  assessment: string
  problems: string[]
  // Optional judge-authored narrowing of the journey description to the implemented scope
  // (src/guidance/journey/generate.ts Bug Report #20198); absent when no revision was supplied.
  revised_description?: string
}

export interface JourneyJudgeSubmission {
  attempt: number
  submitted_at: string
  step_count: number
  old_step_count: number | null
  result: JourneyJudgeResult
}

export interface Journey {
  id: string
  project_id: string
  name: string
  description: string | null
  instructions: string | null
  target_url: string | null
  use_saved_session?: boolean
  mobile: boolean
  polish: boolean
  parent_id: string | null
  source_session_id?: string | null
  child_count: number
  steps: JourneySteps
  summary: string | null
  step_count: number
  versions: JourneyVersion[]
  judge_status?: string | null
  judge_submissions?: JourneyJudgeSubmission[] | null
  test_run_count?: number
  last_run_id?: string | null
  preview_run_id?: string | null
  last_run_at?: string | null
  last_run_status?: string | null
  queue_task_id?: string | null
  queue_priority?: number | null
  queue_rank?: string | null
  last_run_bug_id?: string | null
  last_run_bug_status?: string | null
  last_run_bug_title?: string | null
  created_at: string
  updated_at: string
}

export interface ExplorationLog {
  id: string
  task_id?: string | null
  message: string
  log_type: string
  created_at: string
}

export interface ExplorationJourney {
  journey_id: string
  journey_name: string
  journey_description: string | null
  judge_status: string | null
  version_number: number
  action: 'created' | 'updated'
  step_count: number
  created_at: string
}

export interface Exploration {
  budget?: number | null
  id: string
  project_id: string
  prompt: string
  status: string
  journeys_created: number
  journeys_updated: number
  started_at: string | null
  completed_at: string | null
  created_at: string
  replay_recording_id?: string | null
  driver_recording_id?: string | null
  blocked_kind?: string | null
  blocked_detail?: string | null
  project_name?: string
  target_url?: string | null
  logs?: ExplorationLog[]
  mcp_errors?: Array<{
    id: string
    container_name: string | null
    recording_id: string | null
    tool: string | null
    params: unknown | null
    kind: string | null
    message: string | null
    duration_ms: number | null
    occurred_at: string | null
    created_at: string
  }>
  journeys?: ExplorationJourney[]
  retriggers_from?: Array<{ id: string; retrigger_id?: string; created_at: string; project_id?: string }>
  retriggered_from?: { id: string; original_id?: string; created_at: string; project_id?: string } | null
}

export interface TestSessionExploration {
  blocked_kind?: string | null
  blocked_detail?: string | null
  id: string
  prompt: string
  status: string
  task_id: string | null
}

export interface TestSessionJourneyRun {
  id: string
  attempt_ids: string[]
  journey_id: string | null
  journey_name: string | null
  goal: string | null
  status: string
  error?: string | null
  task_id?: string | null
  priority?: number | null
  queue_rank?: string | null
  bugs_found: number
}

export interface TestSessionBug {
  is_duplicate?: boolean
  id: string
  title: string
  severity: string
  status: string
  test_run_id?: string | null
}

export interface JourneyBug {
  id: string
  title: string
  severity: string
  status: string
  polish_category: string | null
  replay_recording_id: string | null
  test_run_id: string | null
  discovered_at: string
  is_duplicate?: boolean
}

// The prose QA wrote about one round of testing, shown as context above that run's results on the Test
// Run page. `started` describes the change that kicked the round off, `finished` what it found. Written
// server-side by netlify/functions/lib/test-run-notices.ts; `content` is markdown.
export interface TestRunNotice {
  source?: string
  id: string
  kind: 'started' | 'findings' | 'coverage' | 'finished' | 'obstacles'
  content: string
  version_id: string | null
  session_id: string | null
  created_at: string
}

export interface TestSession {
  flags?: TestRunFlags
  id: string
  project_id: string
  version_id: string | null
  ordinal: number
  status: string
  started_at: string
  completed_at: string | null
  prLabel: string | null
  pr_number?: number | null
  pr_repo_full_name?: string | null
  environment_name?: string | null
  environment_kind?: string | null
  /** The version-added task that browsed this round's version — the round's browse pass, replayed in
   *  the exploration slot on the Test Run page. Null when the session has no version. */
  version_task_id: string | null
  title: string
  explorations: TestSessionExploration[]
  journeyRuns: TestSessionJourneyRun[]
  bugs: TestSessionBug[]
  findings?: { new_bugs: TestSessionBug[]; rediscoveries: TestSessionBug[] }
}

export interface TestRunScreenshot {
  id: string
  test_run_id: string
  screenshot_url: string | null
  caption: string | null
  page_url: string | null
  created_at: string
}

export interface TestRunLog {
  id: string
  task_id: string | null
  message: string
  log_type: string
  created_at: string
}

export interface TestRun {
  session_repair?: unknown
  setup_output?: unknown
  outcome?: { status: string; stepIndex?: number; detail?: string; reason?: string; proposedBug?: string | null; judgeProblems?: string[] } | null
  id: string
  project_id: string
  journey_id: string | null
  test_session_id?: string | null
  journey_version_id: string | null
  journey_version_number: number | null
  status: string
  bugs_found_count: number
  replay_recording_id: string | null
  replay_recording_ids: Record<string, string> | null
  setup_recording_id?: string | null
  driver_recording_id?: string | null
  created_at: string
  started_at: string
  completed_at: string | null
  duration_seconds: number | null
  goal: string | null
  state_warnings?: Array<{
    checkpoint: 'event' | 'stream-end'
    step_index: number | null
    source_event_index: number | null
    source_timestamp: number
    similarity: number
    missing: string[]
    added: string[]
  }>
  network_warnings?: Array<{
    kind: 'network-unexpected' | 'network-request-mismatch' | 'network-missing' | 'network-param-substituted' | 'network-request-body-unreadable' | 'network-body-truncated' | 'network-entity-mismatch' | 'network-configured' | 'network-unsupported' | 'network-retained'
    step_index: number | null
    method: string
    url: string
    detail: string
  }>
  project_name?: string
  deployed_url?: string | null
  journey_target_url?: string | null
  environment_id?: string | null
  environment_name?: string | null
  environment_kind?: string | null
  environment_target_url?: string | null
  override_url?: string | null
  target_url?: string | null
  journey_name?: string | null
  journey_judge_status?: string | null
  journey_steps?: unknown
  journey_summary?: string | null
  journey_step_count?: number
  bugs?: Bug[]
  bug_titles?: string[]
  screenshots?: TestRunScreenshot[]
  logs?: TestRunLog[]
  mcp_errors?: Array<{
    id: string
    container_name: string | null
    recording_id: string | null
    tool: string | null
    params: unknown | null
    kind: string | null
    message: string | null
    duration_ms: number | null
    occurred_at: string | null
    created_at: string
  }>
  journey_updates?: Array<{
    id: string
    version_number: number
    name: string
    description: string | null
    steps: unknown
    summary: string | null
    step_count: number
    created_at: string
  }> | null
  retriggers_from?: Array<{ id: string; retrigger_id?: string; created_at: string; project_id?: string }>
  retriggered_from?: { id: string; original_id?: string; created_at: string; project_id?: string } | null
}

export interface PaginatedData<T> {
  items: T[]
  total: number
  totalAll?: number
  statusCounts?: Record<string, number>
  resolvedCount?: number
  page: number
  pageSize: number
}

export interface BugCounts {
  total: number
  journeyBugs: number
  polishBugs: number
  openCount: number
  resolvedCount: number
}
